/**
 * Экран «Отзывы» — лента отзывов + модерация (скрыть) менеджеру/владельцу.
 *
 * Дизайн: manager v3 (lovart-hotels-design-manager-v3-2026-06-06.html),
 * Reviews → moderation interface:
 *   - сводка: средний рейтинг + распределение по статусам;
 *   - карточки отзывов: автор-маркер, звёзды рейтинга, текст, тип объекта,
 *     дата; ответ отеля (если есть); низкие оценки (<3★) подсвечены;
 *   - модерация: кнопка «Скрыть» с указанием причины (POST .../hide).
 *
 * Реальный русский экран поверх готового бэка internal/reviews,
 * а НЕ заглушка.
 *
 * i18n: 100% RU — состояния/типы целей через reviews-format. Мобайл 375:
 * одноколоночный stack, чипы и действия переносятся.
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import { usePlatformMe } from '../../shared/api/usePlatformMe.js';
import { Toast } from '../../shared/ui/Toast.js';
import {
  REVIEW_FILTERS,
  formatDateTime,
  reviewStateLabel,
  reviewStateTone,
  stars,
  targetTypeLabel,
} from './reviews-format.js';
import { StarInput } from './StarInput.js';
import { useCreateReview, useHideReview, useReviews } from './useReviews.js';
import type { Review } from './useReviews.js';

export interface ReviewsPageProps {
  /** Управляющая роль (owner/manager) — показываем действия модерации. */
  canManage?: boolean;
}

function avgRating(items: readonly Review[]): number {
  if (items.length === 0) return 0;
  const sum = items.reduce((a, r) => a + (r.rating || 0), 0);
  return Math.round((sum / items.length) * 10) / 10;
}

/**
 * Один поддомен, роль-роутинг: гость оставляет отзыв + видит свои отзывы;
 * менеджер/владелец — лента + модерация. Per
 * feedback_one_subdomain_per_hotel_role_routing.
 */
export function ReviewsPage({ canManage = false }: ReviewsPageProps): JSX.Element {
  if (!canManage) {
    return <GuestReviews />;
  }
  return <ManagerReviews />;
}

function ManagerReviews(): JSX.Element {
  const [state, setState] = useState('');
  const [hidingId, setHidingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<{ msg: string; tone: 'info' | 'error' } | null>(null);

  const { data, isLoading, isError } = useReviews(state ? { state } : undefined);
  const hide = useHideReview();

  const reviews = useMemo(() => data?.items ?? [], [data]);
  const avg = useMemo(() => avgRating(reviews), [reviews]);

  function submitHide(id: string): void {
    hide.mutate(
      { id, reason: reason.trim() },
      {
        onSuccess: () => {
          setToast({ msg: 'Отзыв скрыт', tone: 'info' });
          setHidingId(null);
          setReason('');
        },
        onError: (e) => setToast({ msg: e.message || 'Не удалось скрыть отзыв', tone: 'error' }),
      },
    );
  }

  // Скрытие отзыва с указанием причины — ОТДЕЛЬНЫЙ ЭКРАН (форма с вводом),
  // список не виден; «Назад» возвращает к списку
  // ([[feedback_every_form_is_a_separate_screen]]).
  const hidingReview =
    hidingId !== null ? reviews.find((r) => r.id === hidingId) ?? null : null;
  if (hidingId !== null) {
    return (
      <section data-testid="reviews-page-hide">
        <div className="ta-topbar">
          <h1 className="hsc-page__title">Скрыть отзыв</h1>
        </div>
        <Card>
          {hidingReview && (
            <div className="rev-card" data-testid="review-hide-target">
              <div className="rev-card__head">
                <span className="rev-stars" aria-label={`${hidingReview.rating} из 5`}>
                  {stars(hidingReview.rating)}
                </span>
                <span className="rev-target">{targetTypeLabel(hidingReview.target_type)}</span>
              </div>
              {hidingReview.text && <p className="rev-text">{hidingReview.text}</p>}
            </div>
          )}
          <div className="rev-hideform" data-testid="review-hideform">
            <label className="hsc-input__label" htmlFor="hide-reason">
              Причина скрытия
            </label>
            <textarea
              id="hide-reason"
              className="ta-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Например: оскорбления / спам"
              rows={3}
            />
            <div className="room-edit__footer">
              <Button
                variant="primary"
                disabled={hide.isPending}
                onClick={() => hidingId !== null && submitHide(hidingId)}
                data-testid="review-hide-confirm"
              >
                {hide.isPending ? 'Скрываю…' : 'Скрыть отзыв'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setHidingId(null);
                  setReason('');
                }}
                data-testid="review-hide-back"
              >
                Назад
              </Button>
            </div>
          </div>
        </Card>
        {toast && <Toast message={toast.msg} tone={toast.tone} onClose={() => setToast(null)} />}
      </section>
    );
  }

  return (
    <section data-testid="reviews-page">
      <h1 className="hsc-page__title">Отзывы</h1>

      {!isLoading && !isError && reviews.length > 0 && (
        <div className="rev-summary" data-testid="reviews-summary">
          <div className="rev-summary__avg">
            <span className="rev-summary__num">{avg.toFixed(1)}</span>
            <span className="rev-stars rev-stars--lg" aria-hidden="true">
              {stars(avg)}
            </span>
            <span className="ta-muted">средняя оценка · {reviews.length} отзыв(ов)</span>
          </div>
        </div>
      )}

      <div className="rev-filters" role="tablist" aria-label="Фильтр отзывов" data-testid="reviews-filters">
        {REVIEW_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            role="tab"
            aria-selected={state === f.value}
            className={`ta-chip rev-chip${state === f.value ? ' is-active' : ''}`}
            data-testid={`reviews-filter-${f.value || 'all'}`}
            onClick={() => setState(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="hsc-stack" data-testid="reviews-loading">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} height={120} radius={16} />
          ))}
        </div>
      ) : isError || !data ? (
        <Card>
          <p role="alert">Не удалось загрузить отзывы. Попробуйте обновить страницу.</p>
        </Card>
      ) : reviews.length === 0 ? (
        <Card>
          <p data-testid="reviews-empty">
            {state ? 'По выбранному фильтру отзывов нет.' : 'Отзывов пока нет — они появятся, когда гости начнут их оставлять.'}
          </p>
        </Card>
      ) : (
        <ul className="hsc-stack" data-testid="reviews-list">
          {reviews.map((rev) => {
            const low = rev.rating > 0 && rev.rating < 3;
            return (
              <li key={rev.id} data-testid={`review-${rev.id}`}>
                <Card>
                  <div className={`rev-card${low ? ' rev-card--low' : ''}`}>
                    <div className="rev-card__head">
                      <span className="rev-stars" aria-label={`${rev.rating} из 5`} data-testid={`review-rating-${rev.id}`}>
                        {stars(rev.rating)}
                      </span>
                      <span className="rev-target">{targetTypeLabel(rev.target_type)}</span>
                      <span
                        className={`fleet-badge fleet-badge--${reviewStateTone(rev.state)}`}
                        data-testid={`review-state-${rev.id}`}
                      >
                        {reviewStateLabel(rev.state)}
                      </span>
                    </div>
                    {rev.text && <p className="rev-text">{rev.text}</p>}
                    <p className="ta-muted rev-meta">{formatDateTime(rev.created_at)}</p>

                    {rev.response && (
                      <div className="rev-reply" data-testid={`review-reply-${rev.id}`}>
                        <p className="rev-reply__head">
                          Ответ отеля{rev.response.author_display ? ` · ${rev.response.author_display}` : ''}
                        </p>
                        <p>{rev.response.text}</p>
                      </div>
                    )}

                    {rev.state === 'published' && (
                      <div className="rev-actions">
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setHidingId(rev.id);
                            setReason('');
                          }}
                          data-testid={`review-hide-${rev.id}`}
                        >
                          Скрыть
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {toast && <Toast message={toast.msg} tone={toast.tone} onClose={() => setToast(null)} />}
    </section>
  );
}

/**
 * Гостевой экран отзывов (дизайн guest v7 §18 «Оставить отзыв»): форма отзыва
 * об отеле (звёзды + текст) + лента своих отзывов с их статусом модерации.
 *
 * target_type='hotel', target_id = id тенанта (отзыв об отеле в целом).
 * Свои отзывы фильтруем по author_user_id == текущий пользователь
 * (бэк ListReviews отдаёт тенант-скоуп без фильтра по автору).
 */
function GuestReviews(): JSX.Element {
  const me = usePlatformMe();
  const { data, isLoading, isError } = useReviews();
  const create = useCreateReview();

  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [toast, setToast] = useState<{ msg: string; tone: 'info' | 'error' } | null>(null);

  const myUserId = me.data?.actor.id;
  const myReviews = useMemo(
    () => (data?.items ?? []).filter((r) => myUserId !== undefined && r.author_user_id === myUserId),
    [data, myUserId],
  );

  const TEXT_MAX = 500;
  const canSubmit = rating > 0 && !create.isPending && me.data !== undefined;

  function submit(): void {
    if (rating <= 0) {
      setToast({ msg: 'Поставьте оценку — хотя бы одну звезду.', tone: 'error' });
      return;
    }
    // target_id — slug тенанта (отель). Бэк хранит target_type+target_id как есть.
    const targetId = me.data?.tenant.slug ?? 'hotel';
    create.mutate(
      { target_type: 'hotel', target_id: targetId, rating, text: text.trim() },
      {
        onSuccess: () => {
          setToast({
            msg: 'Спасибо! Опубликуем после короткой проверки в течение 24 часов.',
            tone: 'info',
          });
          setRating(0);
          setText('');
        },
        onError: (e) => {
          const msg = /already|exists|duplicate/i.test(e.message)
            ? 'Вы уже оставили отзыв об отеле. Спасибо!'
            : e.message || 'Не удалось отправить отзыв.';
          setToast({ msg, tone: 'error' });
        },
      },
    );
  }

  return (
    <section data-testid="reviews-page-guest">
      <h1 className="hsc-page__title">Ваш отзыв</h1>
      <p className="ta-muted">
        Ваш отзыв увидят другие гости — это помогает нам становиться лучше.
      </p>

      <Card>
        <div className="rev-form" data-testid="review-form">
          <div className="rev-form__field">
            <span className="hsc-input__label">Общая оценка</span>
            <StarInput value={rating} onChange={setRating} ariaLabel="Оценка отеля" />
          </div>

          <div className="rev-form__field">
            <label className="hsc-input__label" htmlFor="rev-text">
              Расскажите подробнее (необязательно)
            </label>
            <textarea
              id="rev-text"
              className="ta-textarea"
              rows={4}
              maxLength={TEXT_MAX}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Что особенно понравилось, что улучшить…"
              data-testid="review-text"
            />
            <p className="rev-form__counter ta-muted">
              {text.length} / {TEXT_MAX}
            </p>
          </div>

          <p className="ta-muted rev-form__note">
            Перед публикацией проверим, что отзыв соответствует правилам (до 24 часов).
          </p>

          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={submit}
            data-testid="review-submit"
          >
            {create.isPending ? 'Отправляем…' : 'Опубликовать'}
          </Button>
        </div>
      </Card>

      <h2 className="hsc-section__title" style={{ marginTop: '1.5rem' }}>
        Мои отзывы
      </h2>
      {isLoading ? (
        <div className="hsc-stack" data-testid="my-reviews-loading">
          {Array.from({ length: 2 }, (_, i) => (
            <Skeleton key={i} height={96} radius={16} />
          ))}
        </div>
      ) : isError ? (
        <Card>
          <p role="alert">Не удалось загрузить ваши отзывы.</p>
        </Card>
      ) : myReviews.length === 0 ? (
        <Card>
          <p data-testid="my-reviews-empty">
            Вы ещё не оставляли отзывов. Поделитесь впечатлениями выше.
          </p>
        </Card>
      ) : (
        <ul className="hsc-stack" data-testid="my-reviews-list">
          {myReviews.map((rev) => (
            <li key={rev.id} data-testid={`my-review-${rev.id}`}>
              <Card>
                <div className="rev-card">
                  <div className="rev-card__head">
                    <span className="rev-stars" aria-label={`${rev.rating} из 5`}>
                      {stars(rev.rating)}
                    </span>
                    <span className="rev-target">{targetTypeLabel(rev.target_type)}</span>
                    <span className={`fleet-badge fleet-badge--${reviewStateTone(rev.state)}`}>
                      {reviewStateLabel(rev.state)}
                    </span>
                  </div>
                  {rev.text && <p className="rev-text">{rev.text}</p>}
                  <p className="ta-muted rev-meta">{formatDateTime(rev.created_at)}</p>
                  {rev.response && (
                    <div className="rev-reply" data-testid={`my-review-reply-${rev.id}`}>
                      <p className="rev-reply__head">
                        Ответ отеля{rev.response.author_display ? ` · ${rev.response.author_display}` : ''}
                      </p>
                      <p>{rev.response.text}</p>
                    </div>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {toast && <Toast message={toast.msg} tone={toast.tone} onClose={() => setToast(null)} />}
    </section>
  );
}
